/**
 * Kiểm xe định kỳ thứ Hai hằng tuần (anh Tiến 31/10/2026, DEC-627, docs/phases/P12/kiem-xe-thu-hai.md):
 * "thứ 2 hàng tuần: Dầu máy, Nước làm mát, Phanh, Lốp, Cẩu, Cóc cẩu. Bắt buộc có hình ảnh thực tế. Nhắc liên tục đến khi hoàn thành.
 *  Nhắc → chọn vị trí → thực hiện."
 * Mỗi mục gắn một hoặc nhiều vị trí trên Sơ đồ xe; mỗi vị trí một ảnh chụp tại chỗ + Đạt / Có vấn đề.
 * Kết quả ghi vào sổ theo vị trí (fleet_position_logs, loại "Kiểm tra") nên bấm vị trí trên sơ đồ là thấy lịch sử kiểm tuần.
 */
import { tireAxleRows, type TireAxles } from './fleet-tires';
import type { TirePosition } from './fleet-efficiency';
import type { PositionSlotType } from './fleet-position-history';
import type { VehiclePart } from './vehicle-parts';

export const WEEKLY_CHECK_ITEMS = ['engine_oil', 'coolant', 'brake', 'tire', 'crane', 'outrigger'] as const;
export type WeeklyCheckItem = (typeof WEEKLY_CHECK_ITEMS)[number];
export const WEEKLY_CHECK_ITEM_LABELS: Record<WeeklyCheckItem, string> = {
  engine_oil: 'Dầu máy',
  coolant: 'Nước làm mát',
  brake: 'Phanh',
  tire: 'Lốp',
  crane: 'Cẩu',
  outrigger: 'Cóc cẩu',
};
/** Gợi ý chụp gì (hiện dưới nút chụp) */
export const WEEKLY_CHECK_ITEM_HINTS: Record<WeeklyCheckItem, string> = {
  engine_oil: 'Rút que thăm nhớt, chụp que thấy rõ mức nhớt',
  coolant: 'Mở nắp máy, chụp bình nước phụ thấy rõ vạch MIN / MAX',
  brake: 'Chụp bình dầu phanh / bầu hơi, đạp thử phanh',
  tire: 'Chụp cả bánh thấy gai lốp và mâm (bánh đôi: chụp cả cặp)',
  crane: 'Chụp cần cẩu, ty ben, dây cáp, móc',
  outrigger: 'Chụp chân chống (cóc) đã hạ thử, thấy đế và ty',
};
/** Mục chỉ có ở xe cẩu */
export const WEEKLY_CHECK_CRANE_ITEMS: readonly WeeklyCheckItem[] = ['crane', 'outrigger'];

export const WEEKLY_CHECK_RESULTS = ['ok', 'issue'] as const;
export type WeeklyCheckResult = (typeof WEEKLY_CHECK_RESULTS)[number];
export const WEEKLY_CHECK_RESULT_LABELS: Record<WeeklyCheckResult, string> = { ok: 'Đạt', issue: 'Có vấn đề' };

/** Nhắc tài xế: mỗi giờ một lần trong khung 07:00–18:00 giờ VN, thứ Hai → thứ Bảy, đến khi kiểm đủ. */
export const WEEKLY_CHECK_REMIND = { fromHour: 7, toHour: 18, everyMinutes: 60 } as const;
/** Từ thứ Ba còn xe chưa kiểm đủ → báo người quản lý xe mỗi sáng một lần */
export const WEEKLY_CHECK_ESCALATE_WEEKDAY = 2;

export interface WeeklyCheckSlot {
  item: WeeklyCheckItem;
  slotType: PositionSlotType;
  slot: TirePosition | VehiclePart;
}

const PART_OF: Record<Exclude<WeeklyCheckItem, 'tire'>, VehiclePart> = {
  engine_oil: 'engine_oil',
  coolant: 'coolant',
  brake: 'brake',
  crane: 'crane',
  outrigger: 'outrigger',
};

/**
 * Các vị trí phải kiểm của một xe. Lốp: hai bánh trước + bánh ngoài của mỗi cặp bánh đôi (bánh trong chụp chung cặp,
 * không chụp riêng được); không kiểm lốp dự phòng. Xe không cẩu bỏ Cẩu, Cóc cẩu.
 */
export function weeklyCheckSlots(axles: TireAxles, isCrane: boolean): WeeklyCheckSlot[] {
  const out: WeeklyCheckSlot[] = [];
  for (const item of WEEKLY_CHECK_ITEMS) {
    if (!isCrane && WEEKLY_CHECK_CRANE_ITEMS.includes(item)) continue;
    if (item === 'tire') {
      for (const r of tireAxleRows(axles))
        for (const p of r.positions) if (!p.endsWith('_IN')) out.push({ item, slotType: 'tire', slot: p });
    } else out.push({ item, slotType: 'part', slot: PART_OF[item] });
  }
  return out;
}

/** Bấm bánh trong của cặp bánh đôi → kiểm theo bánh ngoài cùng cặp. */
export const weeklyTireSlot = (p: TirePosition): TirePosition => (p.endsWith('_IN') ? (p.replace('_IN', '_OUT') as TirePosition) : p);

/** Mục của một vị trí (null = vị trí không thuộc bảng kiểm tuần của xe này) */
export function weeklyCheckItemOf(axles: TireAxles, isCrane: boolean, slotType: PositionSlotType, slot: string): WeeklyCheckItem | null {
  const s = slotType === 'tire' ? weeklyTireSlot(slot as TirePosition) : slot;
  return weeklyCheckSlots(axles, isCrane).find((x) => x.slotType === slotType && x.slot === s)?.item ?? null;
}

/** Kết quả đã kiểm của một vị trí trong tuần */
export interface WeeklyCheckDone {
  result: WeeklyCheckResult;
  note: string | null;
  photoId: number;
  at: string;
  byName: string | null;
}
export interface WeeklyCheckSlotState extends WeeklyCheckSlot {
  name: string;
  done: WeeklyCheckDone | null;
}
export interface WeeklyCheckState {
  vehicleId: number;
  plateNo: string;
  axles: TireAxles;
  isCrane: boolean;
  weekStart: string;
  slots: WeeklyCheckSlotState[];
  doneCount: number;
  total: number;
  issues: number;
  completedAt: string | null;
}
export interface WeeklyCheckBoardRow extends WeeklyCheckState {
  drivers: string | null;
}
